/**
 * BMP variant image preload.
 *
 * After the main product image has loaded (and the page has finished loading),
 * quietly fetches each other variant's main image, one at a time, at low
 * priority, so a later colour click shows its photo straight from cache.
 *
 * - Reads inert <template data-bmp-variant-image-preload> img tags rendered by
 *   snippets/product-media-gallery-content.liquid. They carry the gallery's own
 *   srcset + sizes, so the browser picks the same file the gallery will use.
 * - Skips duplicates: one fetch per image file, none for images already shown.
 * - Does nothing on Save-Data, prefers-reduced-data, or 2G connections.
 * - Read-only: no variant, price, form or cart code is touched.
 */

const STARTED = Symbol.for('bmp-variant-image-preload');

function dataSaverOn() {
  const connection = navigator.connection;
  if (connection?.saveData) return true;
  if (connection && /(^|-)2g$/.test(connection.effectiveType || '')) return true;
  return window.matchMedia?.('(prefers-reduced-data: reduce)').matches === true;
}

const idle = (fn) =>
  'requestIdleCallback' in window ? requestIdleCallback(fn, { timeout: 2000 }) : setTimeout(fn, 200);

function whenPageLoaded() {
  return document.readyState === 'complete'
    ? Promise.resolve()
    : new Promise((resolve) => window.addEventListener('load', () => resolve(), { once: true }));
}

function whenMainImageLoaded() {
  const img = document.querySelector('media-gallery .product-media__image');
  if (!img || img.complete) return Promise.resolve();
  return new Promise((resolve) => {
    img.addEventListener('load', () => resolve(), { once: true });
    img.addEventListener('error', () => resolve(), { once: true });
  });
}

// Same file at any width = same image; used to skip ones the gallery already showed.
const fileKey = (url) => {
  try {
    return new URL(url, location.href).pathname;
  } catch {
    return url;
  }
};

function loadOne(source) {
  return new Promise((resolve) => {
    const img = new Image();
    img.fetchPriority = 'low';
    img.decoding = 'async';
    img.addEventListener('load', () => resolve(), { once: true });
    img.addEventListener('error', () => resolve(), { once: true });
    // sizes before srcset so the browser's first pick already uses them.
    if (source.sizes) img.sizes = source.sizes;
    if (source.srcset) img.srcset = source.srcset;
    img.src = source.src;
  });
}

async function run() {
  if (window[STARTED]) return;
  window[STARTED] = true;
  if (dataSaverOn()) return;

  const template = document.querySelector('template[data-bmp-variant-image-preload]');
  if (!template) return;

  await whenPageLoaded();
  await whenMainImageLoaded();

  // Read after load, so a gallery swap that happened meanwhile is respected.
  const live = document.querySelector('template[data-bmp-variant-image-preload]') || template;
  const sources = [...live.content.querySelectorAll('img')].map((img) => ({
    src: img.getAttribute('src'),
    srcset: img.getAttribute('srcset'),
    sizes: img.getAttribute('sizes'),
  }));

  const seen = new Set(
    [...document.querySelectorAll('media-gallery img')]
      .filter((img) => img.complete && img.currentSrc)
      .map((img) => fileKey(img.currentSrc))
  );

  for (const source of sources) {
    const key = fileKey(source.src);
    if (seen.has(key)) continue;
    seen.add(key);
    if (dataSaverOn()) return;
    await new Promise((resolve) => idle(resolve));
    await loadOne(source);
  }
}

run();
